// AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import { Card, Row, Col, Table, Spinner, Container, Badge, Button, Alert } from "react-bootstrap";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useProjectContext } from "../../contextProviders/projectContext";
import NotificationIcon from "../../components/Notification";
import { useUser } from "../../contextProviders/userContext";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import {
  FaUsers,
  FaFolder,
  FaCheck,
  FaTimes,
  FaHourglassHalf,
  FaArrowRight,
  FaAward,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    fetchProjects,
    updateProject
  } = useProjectContext();
  const { user } = useUser();
  const navigate = useNavigate();

  const [reportSummary, setReportSummary] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState("");
  const [showAllInterests, setShowAllInterests] = useState(false);

  useEffect(() => {
    const fetchReportingData = async () => {
      try {
        setReportLoading(true);
        const reportResponse = await api.get("/reports/summary");
        setReportSummary(reportResponse.data);
        setReportError("");
      } catch (err) {
        console.error("Error fetching dashboard reporting data:", err);
        setReportError("Failed to fetch reporting data. Please try again later.");
      } finally {
        setReportLoading(false);
      }
    };
    fetchReportingData();
  }, []);

  if (projectsLoading || reportLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (projectsError || reportError) {
    return (
      <Container className="mt-4">
        <div className="error-message">{projectsError || reportError}</div>
      </Container>
    );
  }

  const totalStudents = reportSummary.metrics.totalStudents;
  const totalProjects = reportSummary.metrics.totalProjects;
  const pendingProjects = projects.filter((p) => p.approved === false);
  const pendingApprovalsCount = reportSummary.metrics.pendingProjects;
  const totalApprovedHours = reportSummary.metrics.approvedHours;

  const recentProjects = projects
    .slice()
    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
    .slice(0, 5)
    .map((p) => ({
      id: p._id,
      name: p.name,
      participants: p.participants.length,
      approved: p.approved
    }));

  const projectStatsArray = reportSummary.distributions.categories;
  const facultyDistribution = reportSummary.distributions.faculties;
  const interestDistribution = reportSummary.distributions.interests;
  const rankedInterests = interestDistribution
    .filter((item) => item.value > 0)
    .slice()
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  const visibleInterests = showAllInterests ? rankedInterests : rankedInterests.slice(0, 8);
  const totalInterestSelections = rankedInterests.reduce((sum, item) => sum + item.value, 0);
  const highestInterestCount = rankedInterests[0]?.value || 0;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const handleApproveReject = async (projectId, isApproved) => {
    try {
      let feedback = "";
      if (!isApproved) {
        const userInput = prompt("Enter a reason/feedback for denying this project proposal:");
        if (userInput === null) return; // User clicked Cancel
        feedback = userInput;
      }
      await updateProject(projectId, { approved: isApproved, feedback });
      fetchProjects();
    } catch (error) {
      console.error("Error updating project approval status:", error);
    }
  };

  // Charts Config
  const facultyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 }
      },
      x: {
        grid: { display: false },
      },
    },
  };

  const facultyChartData = {
    labels: facultyDistribution.map((item) => item.name),
    datasets: [
      {
        label: "Students",
        data: facultyDistribution.map((item) => item.value),
        backgroundColor: "#1B3A87", // Primary Navy
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  };

  const projectStatsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 }
      },
    },
  };

  const projectStatsData = {
    labels: projectStatsArray.map((item) => item.name),
    datasets: [
      {
        label: "Projects Count",
        data: projectStatsArray.map((item) => item.projects),
        backgroundColor: "#1B3A87", // Primary Navy
        borderRadius: 4,
      },
      {
        label: "Approved Hours",
        data: projectStatsArray.map((item) => item.approvedHours),
        backgroundColor: "#2A9D8F", // Teal Accent
        borderRadius: 4,
      },
    ],
  };

  // Styled Cards
  const headerCardStyle = {
    backgroundColor: "var(--color-primary)",
    borderRadius: "var(--radius-lg)",
    border: "none",
    color: "#fff",
    boxShadow: "var(--shadow-sm)",
  };

  const cleanMetricCardStyle = () => ({
    backgroundColor: "var(--color-surface)",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--color-border)",
    boxShadow: "var(--shadow-sm)",
  });

  const chartCardStyle = {
    backgroundColor: "var(--color-surface)",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--color-border)",
    boxShadow: "var(--shadow-sm)",
  };

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{
        maxWidth: "100vw",
        overflowX: "hidden",
      }}>
      {/* Sidebar */}
      <Sidebar />

      <Container
        fluid
        className="py-3 px-4"
        style={{ minHeight: "100vh", maxHeight: "100vh", overflowY: "scroll", backgroundColor: "var(--color-bg)" }}>
        {/* Top Bar */}
        <TopNavBar />

        {/* Banner Welcome Card */}
        <Card className="mb-4 p-4 mt-3" style={headerCardStyle}>
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <div className="text-start">
              <h2 className="fw-bold mb-1">{getGreeting()}, {user?.name || "Admin"}</h2>
              <p className="mb-0 text-white-50">Here is your WCCO volunteer program dashboard overview.</p>
            </div>
            <div className="d-none d-md-block">
              <NotificationIcon />
            </div>
          </div>
        </Card>

        {/* Compact, Denser Metrics Row */}
        <Row className="mb-4">
          <Col xs={6} md={3} className="mb-3">
            <Card style={cleanMetricCardStyle()} className="p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small fw-bold text-uppercase">Total Students</div>
                  <h3 className="fw-bold mt-2 mb-0" style={{ color: "var(--color-text)" }}>{totalStudents}</h3>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", color: "var(--color-primary)" }}>
                  <FaUsers size={20} />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Card style={cleanMetricCardStyle()} className="p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small fw-bold text-uppercase">Total Projects</div>
                  <h3 className="fw-bold mt-2 mb-0" style={{ color: "var(--color-text)" }}>{totalProjects}</h3>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: "rgba(42, 157, 143, 0.08)", color: "var(--color-teal)" }}>
                  <FaFolder size={20} />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Card style={cleanMetricCardStyle()} className="p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small fw-bold text-uppercase">Approved Hours</div>
                  <h3 className="fw-bold mt-2 mb-0" style={{ color: "var(--color-text)" }}>{totalApprovedHours.toFixed(1)} <span style={{ fontSize: "0.9rem", fontWeight: "normal" }}>hrs</span></h3>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: "rgba(181, 131, 42, 0.08)", color: "var(--color-gold)" }}>
                  <FaAward size={20} />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-3">
            <Card style={cleanMetricCardStyle()} className="p-3 h-100">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small fw-bold text-uppercase">Pending Reviews</div>
                  <h3 className="fw-bold mt-2 mb-0" style={{ color: "var(--color-text)" }}>{pendingApprovalsCount}</h3>
                </div>
                <div className="p-3 rounded-circle" style={{ backgroundColor: "rgba(193, 57, 43, 0.08)", color: "var(--color-red)" }}>
                  <FaHourglassHalf size={20} />
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Actionable Review Queue & Overview row */}
        <Row className="mb-4">
          {/* Actionable Pending Approvals Queue */}
          <Col lg={7} className="mb-4">
            <Card style={chartCardStyle} className="p-4 h-100">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>Projects Awaiting Approval</h5>
                <Badge bg="danger" className="px-2 py-1">{pendingApprovalsCount} Pending</Badge>
              </div>

              {pendingApprovalsCount === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 py-4">
                  <Alert variant="success" className="w-100 text-center mb-0" style={{ borderRadius: "10px" }}>
                    🎉 <strong>All Caught Up!</strong> There are no pending project approval requests.
                  </Alert>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "280px", overflowY: "auto" }}>
                  <Table hover align="middle" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Project</th>
                        <th>Organiser</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingProjects.map((proj) => (
                        <tr key={proj._id}>
                          <td>
                            <span 
                              style={{ cursor: "pointer", color: "var(--color-primary)", fontWeight: "600" }}
                              onClick={() => navigate(`/admin/projects/${proj._id}`)}
                              className="hover-underline"
                            >
                              {proj.name}
                            </span>
                            <div className="text-muted small">{proj.categories?.[0] || "Other"}</div>
                          </td>
                          <td>
                            <div className="fw-bold">{proj.organiserName}</div>
                            <div className="text-muted small">{proj.organiserType}</div>
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleApproveReject(proj._id, true)}
                                title="Approve Project"
                                style={{ backgroundColor: "var(--color-teal)", borderColor: "var(--color-teal)" }}
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleApproveReject(proj._id, false)}
                                title="Reject Project"
                                style={{ backgroundColor: "var(--color-red)", borderColor: "var(--color-red)" }}
                              >
                                <FaTimes />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>
          </Col>

          {/* Recent Projects Sidebar Widget */}
          <Col lg={5} className="mb-4">
            <Card style={chartCardStyle} className="p-4 h-100">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>Recent Projects</h5>
                <Button variant="link" size="sm" className="p-0 text-decoration-none" style={{ color: "var(--color-teal)" }} onClick={() => navigate("/admin/projects")}>
                  View All <FaArrowRight className="ms-1" />
                </Button>
              </div>

              {recentProjects.length > 0 ? (
                <div className="table-responsive">
                  <Table hover align="middle" className="mb-0">
                    <tbody>
                      {recentProjects.map((project) => (
                        <tr key={project.id}>
                          <td>
                            <span
                              style={{ cursor: "pointer", color: "var(--color-text)", fontWeight: "600" }}
                              onClick={() => navigate(`/admin/projects/${project.id}`)}
                            >
                              {project.name}
                            </span>
                          </td>
                          <td className="text-end">
                            <Badge className="px-2 py-1 text-white border-0" style={{ backgroundColor: "var(--color-teal)", borderRadius: "var(--radius-sm)" }}>
                              {project.participants} volunteers
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted">No projects registered yet.</p>
              )}
            </Card>
          </Col>
        </Row>

        {/* Charts Section Row - Denser & side-by-side */}
        <Row className="mb-4">
          <Col lg={7} className="mb-4">
            <Card style={chartCardStyle} className="p-4">
              <h5 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Project Statistics by Category</h5>
              <div style={{ height: "260px", width: "100%" }}>
                <Bar options={projectStatsOptions} data={projectStatsData} />
              </div>
            </Card>
          </Col>
          <Col lg={5} className="mb-4">
            <Card style={chartCardStyle} className="p-4">
              <h5 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Faculty Student Distribution</h5>
              <div style={{ height: "260px", width: "100%" }}>
                <Bar options={facultyChartOptions} data={facultyChartData} />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Interests Distribution Row */}
        <Row className="mb-4">
          <Col xs={12}>
            <Card style={chartCardStyle} className="p-4">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <h5 className="fw-bold mb-1" style={{ color: "var(--color-primary)" }}>Volunteering interests</h5>
                  <p className="small text-muted mb-0">Ranked by student selections for quick comparison.</p>
                </div>
                {rankedInterests.length > 0 && (
                  <div className="d-flex gap-4 text-end">
                    <div>
                      <div className="small text-muted">Selections</div>
                      <div className="fw-bold" style={{ color: "var(--color-primary)" }}>{totalInterestSelections}</div>
                    </div>
                    <div>
                      <div className="small text-muted">Interests</div>
                      <div className="fw-bold" style={{ color: "var(--color-primary)" }}>{rankedInterests.length}</div>
                    </div>
                  </div>
                )}
              </div>

              {visibleInterests.length > 0 ? (
                <>
                  <div className="d-flex flex-column gap-3">
                    {visibleInterests.map((item, index) => {
                      const share = totalInterestSelections
                        ? (item.value / totalInterestSelections) * 100
                        : 0;
                      const relativeWidth = highestInterestCount
                        ? (item.value / highestInterestCount) * 100
                        : 0;

                      return (
                        <div key={item.name} className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle small fw-bold"
                            style={{
                              width: 30,
                              height: 30,
                              flexShrink: 0,
                              color: index < 3 ? "#FFFFFF" : "var(--color-text-secondary)",
                              backgroundColor: index < 3 ? "var(--color-primary)" : "var(--color-bg)",
                              border: index < 3 ? "none" : "1px solid var(--color-border)",
                            }}
                          >
                            {index + 1}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-1">
                              <span className="fw-semibold text-truncate" title={item.name}>{item.name}</span>
                              <span className="small text-nowrap">
                                <strong>{item.value}</strong>
                                <span className="text-muted ms-2">{share.toFixed(1)}%</span>
                              </span>
                            </div>
                            <div
                              className="rounded-pill overflow-hidden"
                              style={{ height: 8, backgroundColor: "#E7EBF3" }}
                              role="progressbar"
                              aria-label={`${item.name}: ${item.value} selections`}
                              aria-valuenow={item.value}
                              aria-valuemin="0"
                              aria-valuemax={highestInterestCount}
                            >
                              <div
                                className="h-100 rounded-pill"
                                style={{
                                  width: `${relativeWidth}%`,
                                  backgroundColor: index === 0 ? "var(--color-teal)" : "var(--color-primary)",
                                  opacity: Math.max(0.55, 1 - index * 0.045),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {rankedInterests.length > 8 && (
                    <div className="d-flex justify-content-center mt-4 pt-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="px-4"
                        onClick={() => setShowAllInterests((current) => !current)}
                        aria-expanded={showAllInterests}
                      >
                        {showAllInterests ? (
                          <>Show top 8 <FaChevronUp className="ms-2" /></>
                        ) : (
                          <>View all {rankedInterests.length} interests <FaChevronDown className="ms-2" /></>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted py-5">No volunteering interests data available.</div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminDashboard;
